import { Download, FileUp, Archive } from 'lucide-react-native';
import { exportModule, importModule } from '../../../common/api/endpoints/module-export-import/module-export-import';
import { importModuleBody } from '../../../common/api/endpoints/module-export-import/module-export-import.zod';
import { updateModuleAcrossVersions } from '../../../common/api/endpoints/modules/modules';
import queryClientInstance from '../../../common/context/QueryClient';
import type { ActionHandlerProps, ActionConfig } from '../DataTable/actionTypes';
import log from '../../../common/utils/logger';

// Create a safe filename from a title
const createSafeFilename = (title: string) => title
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_') // Replace any non-alphanumeric chars with underscore
  .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
  .substring(0, 50);

export const moduleActions: Record<string, Partial<ActionConfig>> = {
  toggleDeprecated: {
    id: 'toggleDeprecated',
    label: 'Toggle Deprecated',
    icon: Archive,
    position: 'bulk',
    bulk: true,
    variant: 'solid',
    action: 'secondary',
    handler: async ({ data, utils, objectName }: ActionHandlerProps) => {
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;

      const isBulk = Array.isArray(data);
      const items = (isBulk ? (data as unknown[]) : [data]) as (Record<string, unknown> & { id: string; isDeprecated?: boolean })[];
      const count = items.length;

      showDialog({
        title: isBulk ? `Toggle Deprecated for ${count} Items` : 'Toggle Deprecated',
        message: isBulk
          ? `Are you sure you want to toggle the deprecated status for these ${count} items?`
          : 'Are you sure you want to toggle the deprecated status for this item?',
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Toggle',
            action: 'primary',
            onPress: async () => {
              try {
                utils.setActionRunning(true);
                const promises = items.map((item) => updateModuleAcrossVersions(item.id, {
                  isDeprecated: !item.isDeprecated,
                }));
                await Promise.all(promises);

                showToastMessage(
                  'success',
                  isBulk ? `Deprecated status toggled for ${count} items` : 'Deprecated status toggled successfully',
                );

                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
                }

                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error(`Toggle deprecated ${objectName} error:`, error);
                showToastMessage('error', `Failed to toggle deprecated status: ${(error as Error).message}`);
              } finally {
                utils.setActionRunning(false);
                if (isBulk) {
                  utils.onClearSelection?.();
                  utils.onExitSelectionMode?.();
                }
              }
            },
          },
        ],
      });
    },
  },
  jsonImport: {
    id: 'jsonImport',
    label: 'Import',
    icon: FileUp,
    position: 'header',
    variant: 'outline',
    action: 'secondary',
    handler: async ({ utils }: ActionHandlerProps) => {
      try {
        // Create file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';

        // Handle file selection
        const handleChange = async (e: Event) => {
          const target = e.target as HTMLInputElement;
          const file = target.files?.[0];
          if (!file) {
            document.body.removeChild(input);
            return;
          }

          try {
            const reader = new FileReader();
            reader.onload = async (event: ProgressEvent<FileReader>) => {
              try {
                const result = event.target?.result as string;
                const jsonData = JSON.parse(result);

                // Validate the data structure using zod
                try {
                  importModuleBody.parse({ data: jsonData });
                } catch (validationError) {
                  // eslint-disable-line @typescript-eslint/no-explicit-any
                  utils.showDialog({
                    title: 'Invalid Module Format',
                    message: `The module file has an invalid format: ${validationError.errors
                      .map((err: any) => err.message) // eslint-disable-line @typescript-eslint/no-explicit-any
                      .join(', ')}`,
                    buttons: [{ text: 'OK', action: 'primary' }],
                  });
                  document.body.removeChild(input);
                  return;
                }

                // Show loading dialog
                utils.showDialog({
                  title: 'Importing Module',
                  message: 'Please wait while the module is being imported...',
                  buttons: [],
                });

                await importModule({ data: jsonData });

                // Hide the loading dialog
                utils.hideDialog();

                // Invalidate all module-related queries
                await queryClientInstance.invalidateQueries({ queryKey: ['/modules'] });

                utils.showToastMessage('success', 'Module imported successfully');
              } catch (error) {
                // eslint-disable-line @typescript-eslint/no-explicit-any
                utils.showDialog({
                  title: 'Import Error',
                  message: `Failed to import module: ${
                    error.response?.data?.message || error.message
                  }. Please check if the file format is correct.`,
                  buttons: [{ text: 'OK', action: 'primary' }],
                });
              }
              document.body.removeChild(input);
            };
            reader.readAsText(file);
          } catch (error) {
            // eslint-disable-line @typescript-eslint/no-explicit-any
            utils.showDialog({
              title: 'Import Error',
              message: 'Failed to read the file. Please make sure it is a valid JSON file.',
              buttons: [{ text: 'OK', action: 'primary' }],
            });
            document.body.removeChild(input);
          }
        };

        // Add input to DOM and set up event listener
        input.style.display = 'none';
        document.body.appendChild(input);
        input.addEventListener('change', handleChange);

        // Trigger file dialog
        input.click();
      } catch (error) {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        log.error('Import error:', error);
        utils.showDialog({
          title: 'Import Error',
          message: `Failed to start import: ${error.message}`,
          buttons: [{ text: 'OK', action: 'primary' }],
        });
      }
    },
  },
  jsonExport: {
    id: 'jsonExport',
    label: 'Export',
    icon: Download,
    variant: 'outline',
    action: 'secondary',
    bulk: true,
    handler: async ({ data, utils }: ActionHandlerProps) => {
      try {
        const isBulk = Array.isArray(data);
        const items = (isBulk ? data : [data]) as any[]; // eslint-disable-line @typescript-eslint/no-explicit-any

        const promises = items.map(async (item) => {
          const moduleId = String(item?.id);
          const moduleTitle = item?.title || moduleId;
          const safeTitle = createSafeFilename(moduleTitle);

          // Call the export endpoint using orval generated function
          const response = await exportModule(moduleId);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const exportData = (response as any).data.data;

          const jsonStr = JSON.stringify(exportData, null, 4);
          const blob = new Blob([jsonStr], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `module_${safeTitle}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        });

        await Promise.all(promises);

        utils.showToastMessage('success', isBulk ? `${items.length} modules exported` : 'Module data downloaded');
      } catch (error) {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        log.error('Export error:', error);
        utils.showToastMessage('error', `Failed to export module: ${error.message}`);
      }
    },
  },
};
